import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Cloud, RefreshCw, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ServiceNowTicketDetail() {
  const { sysId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [workNotes, setWorkNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingWorkNotes, setLoadingWorkNotes] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTicketDetails();
  }, [sysId]);

  const fetchTicketDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all incidents and find the one with matching sys_id
      const response = await axios.get(`${API}/servicenow/incidents`);
      const foundTicket = response.data.find(t => t.servicenow_sys_id === sysId);
      
      if (foundTicket) {
        setTicket(foundTicket);
        // Also fetch work notes
        fetchWorkNotes();
      } else {
        setError('Ticket not found');
      }
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError('Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkNotes = async () => {
    setLoadingWorkNotes(true);
    try {
      const response = await axios.get(`${API}/servicenow/incidents/${sysId}/worknotes`);
      setWorkNotes(response.data.work_notes || '');
    } catch (err) {
      console.error('Error fetching work notes:', err);
    } finally {
      setLoadingWorkNotes(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-blue-500',
      'in_analysis': 'bg-purple-500',
      'in_development': 'bg-indigo-500',
      'in_testing': 'bg-amber-500',
      'in_governance': 'bg-cyan-500',
      'completed': 'bg-green-500',
      'rejected': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'critical': 'border-red-500 text-red-600 bg-red-50',
      'high': 'border-orange-400 text-orange-600 bg-orange-50',
      'medium': 'border-yellow-400 text-yellow-600 bg-yellow-50',
      'low': 'border-green-400 text-green-600 bg-green-50'
    };
    return colors[priority] || 'border-gray-400 text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading ticket details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/tickets')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tickets
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={fetchTicketDetails}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="space-y-6" data-testid="servicenow-ticket-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/tickets')} data-testid="back-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono bg-blue-50">{ticket.ticket_number}</Badge>
              <Badge className={getStatusColor(ticket.status)}>{ticket.status.replace('_', ' ')}</Badge>
              <Badge variant="outline" className={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge>
              <Badge variant="outline" className="bg-green-50">
                <Cloud className="h-3 w-3 mr-1" />
                ServiceNow
              </Badge>
            </div>
            <h1 className="text-2xl font-bold">{ticket.title}</h1>
          </div>
        </div>
        <Button variant="outline" onClick={fetchTicketDetails}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Ticket Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Ticket Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Primary Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Ticket Number</div>
                <div className="font-medium font-mono">{ticket.ticket_number}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Company</div>
                <div className="font-medium">{ticket.company || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Category</div>
                <div className="font-medium">{ticket.category || ticket.customization_type || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Priority</div>
                <Badge variant="outline" className={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge>
              </div>
            </div>

            {/* Short Description */}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Short Description</div>
              <div className="font-medium">{ticket.short_description || ticket.title}</div>
            </div>

            {/* Full Description */}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Description</div>
              <div className="font-medium whitespace-pre-wrap bg-slate-50 p-3 rounded-lg border">
                {ticket.description || 'No description provided'}
              </div>
            </div>

            {/* Work Notes */}
            <div>
              <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                Work Notes
                {loadingWorkNotes && <Loader2 className="h-3 w-3 animate-spin" />}
              </div>
              {workNotes ? (
                ticket.category === 'Code Report' ? (
                  // Code block display for Code Report tickets
                  <div className="bg-slate-900 rounded-lg border overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
                      <span className="text-xs text-slate-400 font-medium">Generated Code / Analysis</span>
                      <Badge variant="outline" className="text-xs bg-green-900 text-green-300 border-green-700">Code Report</Badge>
                    </div>
                    <pre className="p-4 overflow-auto max-h-96 text-sm">
                      <code className="text-slate-100 font-mono whitespace-pre-wrap">{workNotes}</code>
                    </pre>
                  </div>
                ) : (
                  // Plain text display for other tickets
                  <div className="font-medium whitespace-pre-wrap bg-amber-50 p-3 rounded-lg border border-amber-200 max-h-64 overflow-auto text-sm">
                    {workNotes}
                  </div>
                )
              ) : (
                <div className="text-sm text-muted-foreground bg-slate-50 p-3 rounded-lg border">
                  No work notes available
                </div>
              )}
            </div>

            {/* Secondary Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t">
              <div>
                <div className="text-sm text-muted-foreground mb-1">Assignment Group</div>
                <div className="font-medium">{ticket.assignment_group || 'Unassigned'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Created By</div>
                <div className="font-medium">{ticket.created_by || ticket.requester_name || 'Unknown'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Created Date</div>
                <div className="font-medium">{new Date(ticket.created_at).toLocaleString()}</div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
              <div>
                <div className="text-sm text-muted-foreground mb-1">SAP System</div>
                <div className="font-medium">{ticket.sap_system}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Source</div>
                <div className="font-medium">{ticket.source}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Urgency</div>
                <div className="font-medium">{ticket.urgency || 'N/A'}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Impact</div>
                <div className="font-medium">{ticket.impact || 'N/A'}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
